import React, { useEffect, useRef } from "react";
import { ErrorMessage, Form, Formik, useFormikContext } from "formik";
import type { FormikContextType } from "formik";
import { ErrorBlockGeneric } from "common-tailwind-components";
import { useTranslation } from "react-i18next";

import { splitApiRequestFormErrors } from "../../api";

type AutoSaveStatus = {
  state: "idle" | "saved" | "failed";
  error: string | null;
};

const idleStatus: AutoSaveStatus = { state: "idle", error: null };

const useAutoSaveFormik = <V extends Record<string, unknown>>() =>
  useFormikContext<V>() as FormikContextType<V> & { status: AutoSaveStatus };

/**
 * Submits as soon as a value changes. A failed save parks the form until the
 * user either edits again (which resets the status) or hits retry.
 */
const AutoSubmitOnChange = () => {
  const { dirty, isSubmitting, isValid, status, submitForm } = useAutoSaveFormik();

  useEffect(() => {
    if (!dirty || !isValid || isSubmitting || status.state === "failed") return;

    void submitForm();
  }, [dirty, isSubmitting, isValid, status.state, submitForm]);

  return null;
};

export type SettingsAutoSaveFormApi<V> = {
  values: V;
  isSubmitting: boolean;
  /** Set a field and clear any stale error/saved state so the auto-save re-arms. */
  setField: <K extends keyof V & string>(name: K, value: V[K]) => void;
};

const shallowEqual = <V extends Record<string, unknown>>(a: V, b: V) =>
  Object.keys(a).every(key => Object.is(a[key], b[key]));

/**
 * Formik form for a group of settings that persist on every change: the save
 * errors are split into field errors and a generic remainder, and the form is
 * reset to whatever the backend persisted. Controls stay enabled while a save
 * is in flight (disabling a focused control would drop keyboard focus); an
 * edit made meanwhile is kept and saved by the next submit.
 */
export const SettingsAutoSaveForm = <V extends Record<string, unknown>>({
  initialValues,
  save,
  formFields,
  fieldAliases,
  children,
}: {
  initialValues: V;
  save: (values: V) => Promise<V>;
  formFields: readonly (keyof V & string)[];
  /** API field name → form field name, for fields the backend names differently. */
  fieldAliases?: Partial<Record<string, keyof V & string>>;
  children: (form: SettingsAutoSaveFormApi<V>) => React.ReactNode;
}) => {
  const { t } = useTranslation();
  const latestValues = useRef(initialValues);
  return (
    <Formik<V>
      initialValues={initialValues}
      initialStatus={idleStatus}
      onSubmit={async (values, actions) => {
        actions.setStatus(idleStatus);
        actions.setErrors({});
        try {
          const persistedValues = await save(values);
          actions.resetForm({ values: persistedValues });
          if (shallowEqual(latestValues.current, values)) {
            actions.setStatus({ state: "saved", error: null });
          } else {
            // Edited while saving: re-arm with the newer values (dirty again).
            actions.setValues(latestValues.current);
          }
        } catch (error: unknown) {
          const parsed = splitApiRequestFormErrors<V>(error, t, formFields, fieldAliases);
          actions.setTouched(parsed.touched, false);
          actions.setErrors(parsed.fieldErrors);
          actions.setStatus({ state: "failed", error: parsed.remainder });
        }
      }}
    >
      {({ isSubmitting, setErrors, setFieldValue, setStatus, values }) => {
        latestValues.current = values;
        return (
          <Form>
            <AutoSubmitOnChange />
            {children({
              values,
              isSubmitting,
              setField: (name, value) => {
                setErrors({});
                setStatus(idleStatus);
                void setFieldValue(name, value);
              },
            })}
          </Form>
        );
      }}
    </Formik>
  );
};

export const SettingsFieldError = ({ name }: { name: string }) => (
  <ErrorMessage name={name}>
    {message => (
      <div role="alert" className="mt-1 text-xs text-danger">
        {message}
      </div>
    )}
  </ErrorMessage>
);

/** Saving/saved hint, generic error and retry button for a SettingsAutoSaveForm. */
export const SettingsAutoSaveFeedback = () => {
  const { t } = useTranslation();
  const { dirty, isSubmitting, status, submitForm } = useAutoSaveFormik();
  const message =
    status.state !== "failed" && (dirty || isSubmitting)
      ? t("settings.saving")
      : status.state === "saved"
        ? t("settings.saved")
        : null;
  return (
    <div className="relative">
      {/* The saving/saved hint floats below the control (in the row's bottom
          padding) so rows never change height as it comes and goes. */}
      <div
        aria-live="polite"
        className={`pointer-events-none absolute right-0 top-0 mt-0.5 text-right text-xs leading-none text-text-mid transition-opacity duration-300 motion-reduce:transition-none ${
          message ? "opacity-100" : "opacity-0"
        }`}
      >
        {message}
      </div>
      <ErrorBlockGeneric error={status.error} />
      {status.state === "failed" && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => void submitForm()}
            disabled={isSubmitting}
            className="min-h-9 rounded-full border border-outline bg-surface px-3 text-xs font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("retry")}
          </button>
        </div>
      )}
    </div>
  );
};
